import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, MapPin, CheckCircle2, Plus } from "lucide-react";
import Seo from "../components/Seo";
import { PageHeader } from "../components/sections/Shared";
import { Button } from "../components/ui/button";
import { SEO_CITIES, SEO_SERVICES } from "../data/seo";
import { COMPANY } from "../data/mock";

export default function CityPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const city = SEO_CITIES.find((c) => c.slug === slug);
  if (!city) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${COMPANY.name} — ${city.city}`,
    telephone: "+1-518-954-4701",
    url: `https://518courier.com/${city.slug}`,
    logo: "https://518courier.com/logo-518.png",
    areaServed: {
      "@type": "City",
      name: city.city,
      containedInPlace: { "@type": "State", name: "New York" }
    },
    description: city.description
  };

  return (
    <>
      <Seo
        title={city.title}
        description={city.description}
        path={`/${city.slug}`}
        jsonLd={jsonLd}
      />

      <PageHeader
        eyebrow="Local service"
        title={`Courier service in`}
        accent={`${city.city}, NY.`}
        description={city.intro}
        image="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzgwMjUzNDA5fDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="container-518 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">
              Services available in {city.city}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Every 518 Courier service is available for pickups and drop-offs in {city.city} and
              the surrounding area. Choose the option that matches your delivery:
            </p>
            <ul className="grid sm:grid-cols-2 gap-2.5 mb-8">
              {SEO_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="flex items-start gap-2 text-slate-700 hover:text-[#f59321] font-medium">
                    <CheckCircle2 className="w-5 h-5 text-[#f59321] shrink-0 mt-0.5" />
                    <span>{s.h1}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">
              Who we serve in {city.city}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">{city.customers}</p>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">
              Common local delivery needs
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">{city.needs}</p>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">
              Nearby communities we also serve
            </h2>
            <div className="flex flex-wrap gap-2 mb-10">
              {city.nearby.map((n) => (
                <span key={n} className="px-3 py-1.5 rounded-full bg-slate-100 text-sm font-semibold text-[#0f2847] border border-slate-200">
                  <MapPin className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-[#f59321]" />
                  {n}
                </span>
              ))}
            </div>

            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-4">
              {city.city} FAQs
            </h2>
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              {city.faqs.map((f, i) => (
                <details key={i} className="group py-4 cursor-pointer">
                  <summary className="flex items-center justify-between gap-4 list-none">
                    <h3 className="font-display font-bold text-[#0f2847]">{f.q}</h3>
                    <Plus className="w-5 h-5 text-[#f59321] group-open:rotate-45 transition-transform shrink-0" />
                  </summary>
                  <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-28 self-start">
            <div className="bg-[#0f2847] text-white rounded-2xl p-6 stripe-bg">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-2">Request pickup</div>
              <h3 className="font-display text-xl font-extrabold mb-3">Serving {city.city} today</h3>
              <p className="text-sm text-slate-300 mb-5 leading-relaxed">
                Tell us what you need moved and we'll dispatch the nearest available driver.
              </p>
              <Link to="/get-a-quote" className="block">
                <Button className="w-full bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-12 font-semibold btn-lift">
                  Get a Quote
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <a href="tel:5189544701" className="mt-3 block">
                <Button variant="outline" className="w-full bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-12 font-semibold">
                  <Phone className="w-4 h-4 mr-2" />
                  Call or text 518-954-4701
                </Button>
              </a>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h4 className="font-display font-bold text-[#0f2847] mb-3 text-sm uppercase tracking-[0.16em]">Other Capital Region cities</h4>
              <ul className="space-y-2">
                {SEO_CITIES.filter((c) => c.slug !== slug).slice(0, 6).map((c) => (
                  <li key={c.slug}>
                    <Link to={`/${c.slug}`} className="text-sm text-slate-700 hover:text-[#f59321] font-medium">
                      → {c.city}, NY
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
